/** A project's notes, shared by everything that reads them: the Notes tab,
 *  the unread badge, and whatever opens next. One fetch per project at a
 *  time, the result kept in memory for the session and in localStorage across
 *  reloads, so a tab opening onto a project it has seen before paints from
 *  what it has and revalidates behind. The API sits behind a per-request
 *  worker boot of about a second, so "fetch on mount, show a spinner" was a
 *  second of spinner on every visit. */

import { API_BASE, getAuthHeaders } from '../utils/supabase/client';

import type { Note } from '../types/projectNote';
export type { EditHistoryEntry, Note, NoteCategory } from '../types/projectNote';

type Listener = (notes: Note[]) => void;

export const NOTES_FRESH_MS = 15_000;

const STORAGE_PREFIX = 'quantis-project-notes-v1:';
const memory = new Map<string, Note[]>();
const fetchedAt = new Map<string, number>();
/** Bumped by every write into the cache; a fetch that started before a
 *  write landed must not overwrite it with the older server snapshot. */
const generation = new Map<string, number>();
const inFlight = new Map<string, Promise<Note[]>>();
const listeners = new Map<string, Set<Listener>>();

/** The persisted copy is scoped to the signed-in user (AuthContext keeps the
 *  user under 'auth_user'), so another account on the same browser can never
 *  read a previous account's notes, even before sign-out has run its clears. */
function currentUserId(): string {
  try {
    const raw = localStorage.getItem('auth_user');
    const id = raw ? (JSON.parse(raw) as { id?: unknown })?.id : undefined;
    return typeof id === 'string' && id ? id : 'anonymous';
  } catch {
    return 'anonymous';
  }
}

function storageKey(projectId: string): string {
  return `${STORAGE_PREFIX}${currentUserId()}:${projectId}`;
}

function notify(projectId: string, notes: Note[]): void {
  listeners.get(projectId)?.forEach((listener) => listener(notes));
}

/** What we already have for the project, or undefined on a cold device. */
export function getCachedNotes(projectId: string): Note[] | undefined {
  const held = memory.get(projectId);
  if (held) return held;
  try {
    const raw = localStorage.getItem(storageKey(projectId));
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return undefined;
    memory.set(projectId, parsed as Note[]);
    return parsed as Note[];
  } catch {
    return undefined;
  }
}

export function setCachedNotes(projectId: string, notes: Note[]): void {
  memory.set(projectId, notes);
  generation.set(projectId, (generation.get(projectId) ?? 0) + 1);
  try {
    localStorage.setItem(storageKey(projectId), JSON.stringify(notes));
  } catch {
    // Quota or private mode: the in-memory copy still serves the session.
  }
  notify(projectId, notes);
}

export function updateCachedNotes(projectId: string, update: (previous: Note[]) => Note[]): void {
  setCachedNotes(projectId, update(getCachedNotes(projectId) ?? []));
}

export function subscribeNotes(projectId: string, listener: Listener): () => void {
  let set = listeners.get(projectId);
  if (!set) {
    set = new Set();
    listeners.set(projectId, set);
  }
  set.add(listener);
  return () => {
    set?.delete(listener);
    if (set?.size === 0) listeners.delete(projectId);
  };
}

/** Fetch the project's notes, sharing an in-flight request between callers
 *  (the badge and the tab used to race three identical requests at once).
 *  With maxAgeMs, a copy fetched more recently than that is returned as is:
 *  the tab mounting seconds after the badge fetched, or a window coming back
 *  to the front, need not pay for another round trip. A realtime "changed"
 *  announcement passes nothing and always goes to the network. */
export function loadProjectNotes(projectId: string, options: { maxAgeMs?: number } = {}): Promise<Note[]> {
  const pending = inFlight.get(projectId);
  if (pending) return pending;

  if (options.maxAgeMs !== undefined) {
    const held = getCachedNotes(projectId);
    const at = fetchedAt.get(projectId);
    if (held && at !== undefined && Date.now() - at < options.maxAgeMs) return Promise.resolve(held);
  }

  const startedAtGeneration = generation.get(projectId) ?? 0;
  const request = (async () => {
    // no-store: the payload's shape can change under us (the server started
    // attaching author avatars), and a cached 200 from before that keeps
    // serving notes with no picture on a device that has one.
    const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });
    const res = await fetch(`${API_BASE}/projects/${projectId}/notes`, { headers, cache: 'no-store' });
    if (!res.ok) {
      // Access withdrawn (or never there): what we hold must go too, or the
      // notes stay readable on this device after the server stopped serving them.
      if (res.status === 401 || res.status === 403) evictProjectNotes(projectId);
      const text = await res.text();
      throw new Error(`Server returned ${res.status}: ${text}`);
    }
    const data = await res.json();
    const notes: Note[] = Array.isArray(data?.notes) ? data.notes : [];
    fetchedAt.set(projectId, Date.now());
    // A pin, edit, create or delete that landed while this was in flight is
    // newer than the snapshot; keep it and let the next revalidate catch up.
    if ((generation.get(projectId) ?? 0) !== startedAtGeneration) return getCachedNotes(projectId) ?? notes;
    setCachedNotes(projectId, notes);
    return notes;
  })();

  inFlight.set(projectId, request);
  request.finally(() => {
    if (inFlight.get(projectId) === request) inFlight.delete(projectId);
  }).catch(() => {});
  return request;
}

function evictProjectNotes(projectId: string): void {
  memory.delete(projectId);
  fetchedAt.delete(projectId);
  generation.set(projectId, (generation.get(projectId) ?? 0) + 1);
  try {
    localStorage.removeItem(storageKey(projectId));
  } catch {
    // No storage.
  }
  notify(projectId, []);
}

/** Everything stored for every project, for sign-out. */
export function clearProjectNotesCache(): void {
  memory.clear();
  fetchedAt.clear();
  generation.clear();
  inFlight.clear();
  listeners.clear();
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) doomed.push(key);
    }
    doomed.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Nothing to clear, or no storage.
  }
}
